import {PackagesItemByPerson, PackagesItemsPerson} from "types/entities/PackagesItems";
import {Timeslot} from "types/entities/Timeslot";
import Dates from "utils/Dates";

/**
 * Returns whether an API package item is a selectable non-karting package.
 * This is deliberately independent of the special one-hour time rule.
 */
export const isSelectablePackageItem = (item: PackagesItemsPerson | undefined): item is PackagesItemsPerson => {
    return !!item && !item.karting && typeof item.duration === "number" && Number.isFinite(item.duration);
};

/**
 * Returns whether a package item can be used for a multi-option duration
 * selection at the currently selected timeslot.
 *
 * A missing timeslot is treated conservatively for the special one-hour
 * package. This prevents initialization from depending on the machine clock.
 */
export const isEligiblePackageItem = (item: PackagesItemsPerson | undefined, selectedTimeslot?: Timeslot | null): boolean => {
    if (!isSelectablePackageItem(item)) {
        return false;
    }

    if (item.duration !== 60) {
        return true;
    }

    return !!selectedTimeslot && Dates.hour(selectedTimeslot.start_at) >= 17;
};

/**
 * Finds the package item to use for fresh per-person initialization.
 *
 * A single effective API item is authoritative, including when it is the
 * one-hour package. The existing one-hour rule remains in force only when
 * choosing among multiple available items.
 */
export const getHighestAvailablePackageItem = (
    items: PackagesItemsPerson[] | undefined,
    selectedTimeslot?: Timeslot | null
): PackagesItemsPerson | undefined => {
    const selectableItems = (items || []).filter(isSelectablePackageItem);

    if (selectableItems.length === 1) {
        return selectableItems[0];
    }

    return selectableItems.reduce<PackagesItemsPerson | undefined>((highest, item) => {
        if (!isEligiblePackageItem(item, selectedTimeslot)) {
            return highest;
        }

        return !highest || item.duration > highest.duration ? item : highest;
    }, undefined);
};

/**
 * Finds the numerically greatest eligible duration in an availability list.
 * The input list is never mutated and its ordering has no effect on the result.
 */
export const getHighestAvailableDuration = (items: PackagesItemsPerson[] | undefined, selectedTimeslot?: Timeslot | null): number | undefined => {
    return getHighestAvailablePackageItem(items, selectedTimeslot)?.duration;
};

/**
 * Keeps an existing selection during initialization and uses the resolved
 * default only for a missing selection.
 */
export const getInitialPackageItemId = (
    selectedPackageItem: PackagesItemByPerson | undefined,
    defaultPackageItem: PackagesItemsPerson | undefined
): number | null => selectedPackageItem?.id ?? defaultPackageItem?.id ?? null;

/**
 * Resolves a requested duration using the existing preferred-order fallback.
 * This is intentionally separate from fresh default selection.
 */
export const resolveRequestedDuration = (
    preferred: number | undefined,
    durations: number[],
    fallbackDuration: number,
    getItem: (duration: number) => PackagesItemsPerson | undefined,
    selectedTimeslot?: Timeslot | null
): number | undefined => {
    const priority = preferred && durations.includes(preferred) ? preferred : fallbackDuration;
    const order = [priority, ...durations.filter(duration => duration !== priority)];

    return order.find(duration => isEligiblePackageItem(getItem(duration), selectedTimeslot));
};

/**
 * Finds the numerically greatest duration shared by every availability list.
 * An empty list, or no common duration, resolves to undefined.
 */
export const getHighestCommonAvailableDuration = (itemsByPersonType: PackagesItemsPerson[][], selectedTimeslot?: Timeslot | null): number | undefined => {
    if (itemsByPersonType.length === 0) {
        return undefined;
    }

    const selectableItemsByPersonType = itemsByPersonType.map(items => items.filter(isSelectablePackageItem));
    const availableDurations = selectableItemsByPersonType.map(items => new Set(items.map(item => item.duration)));

    if (availableDurations.some(durations => durations.size === 0)) {
        return undefined;
    }

    const commonDurations = Array.from(availableDurations[0]).filter(duration => availableDurations.every(durations => durations.has(duration)));

    // The only common API duration is authoritative, including a lone
    // one-hour package that the secondary multi-option rule would reject.
    if (commonDurations.length === 1) {
        return commonDurations[0];
    }

    return commonDurations.reduce<number | undefined>((highest, duration) => {
        const isEligibleForEveryPerson = itemsByPersonType.every(items =>
            items.some(item => item.duration === duration && isEligiblePackageItem(item, selectedTimeslot))
        );

        if (!isEligibleForEveryPerson) {
            return highest;
        }

        return highest === undefined || duration > highest ? duration : highest;
    }, undefined);
};
